import { useEffect, useMemo, useRef } from "react";
import JsBarcode from "jsbarcode";
import { Button, Modal } from "@shop-ledger/ui-components";

export interface LabelEntry {
  itemId: string;
  name: string;
  sku: string;
  priceLabel: string;
}

export interface BulkLabelPrintModalProps {
  open: boolean;
  onClose: () => void;
  /** Already expanded: one entry per physical copy (a selected item repeated `copies`
   * times), flat, in selection order — the expansion happens in `LabelsPage`, so this
   * component stays a pure renderer. */
  labels: LabelEntry[];
  columnsPerPage: number;
  rowsPerPage: number;
  includePrice: boolean;
}

function LabelCell({ label, includePrice }: { label: LabelEntry; includePrice: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;
    JsBarcode(svgRef.current, label.sku, {
      format: "CODE128",
      width: 2,
      height: 40,
      fontSize: 12,
      margin: 4,
    });
  }, [label.sku]);

  return (
    <div className="sl-print-label-sheet__cell">
      <div className="sl-print-label-sheet__name">{label.name}</div>
      <svg ref={svgRef} />
      {includePrice ? <div className="sl-print-label-sheet__price">{label.priceLabel}</div> : null}
    </div>
  );
}

/**
 * Bulk sibling of `LabelPrintModal.tsx` — same Code128/`jsbarcode` + `window.print()` +
 * visibility-isolation printing mechanism (`.sl-print-label-sheet` in `index.css`), extended
 * to N labels chunked into `rowsPerPage x columnsPerPage`-sized pages with a page break
 * between each. The barcode value is each product's `sku` directly (no `Barcode` row
 * needed). Grid dimensions come from user input at print time, so — same reasoning as
 * `DocumentGeneratorPage.tsx`'s inline `@page` rule — they're set via a component-scoped
 * `<style>` tag here rather than a static `index.css` rule.
 */
export function BulkLabelPrintModal({
  open,
  onClose,
  labels,
  columnsPerPage,
  rowsPerPage,
  includePrice,
}: BulkLabelPrintModalProps) {
  const perPage = Math.max(1, rowsPerPage * columnsPerPage);
  const pages = useMemo(() => {
    const out: LabelEntry[][] = [];
    for (let i = 0; i < labels.length; i += perPage) out.push(labels.slice(i, i + perPage));
    return out;
  }, [labels, perPage]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Print labels"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button type="button" variant="primary" onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </>
      }
    >
      <p className="muted">{labels.length} label(s) across {pages.length} page(s).</p>
      <style>{`
        .sl-print-label-sheet__page {
          display: grid;
          grid-template-columns: repeat(${columnsPerPage}, 1fr);
        }
      `}</style>
      <div className="sl-print-label-sheet">
        {pages.map((page, i) => (
          <div key={i} className="sl-print-label-sheet__page">
            {page.map((label, j) => (
              <LabelCell key={`${label.itemId}-${i}-${j}`} label={label} includePrice={includePrice} />
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
}
